import api from '../../../services/api';
import axios from 'axios';

/**
 * Authentication Service
 * Handles all auth-related API calls
 */
class AuthService {
  /**
   * Send OTP for customer registration (validates data but doesn't save user)
   */
  async sendRegistrationOTP(userData) {
    try {
      const response = await api.post('/auth/send-registration-otp', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Register new customer (after OTP verification)
   */
  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Admin login with role validation
   */
  async adminLogin(email, password, twoFactorCode = null, rememberMe = false) {
    try {
      const payload = { email, password, rememberMe };
      if (twoFactorCode) {
        payload.twoFactorCode = twoFactorCode;
      }
      
      const response = await api.post('/auth/admin-login', payload, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send OTP for customer login
   */
  async sendLoginOTP(phone) {
    try {
      const response = await api.post('/auth/send-login-otp', { phone }, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify OTP and login customer
   */
  async verifyLoginOTP(phone, otp) {
    try {
      const response = await api.post('/auth/verify-login-otp', { phone, otp }, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send OTP for technician login
   */
  async sendTechnicianLoginOTP(phone) {
    try {
      const response = await api.post('/auth/send-technician-login-otp', { phone }, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify OTP and login technician
   */
  async verifyTechnicianLoginOTP(phone, otp) {
    try {
      const response = await api.post('/auth/verify-technician-login-otp', { phone, otp }, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout current user
   */
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      // Use direct axios call to avoid interceptor logic that might redirect to login
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(`${API_URL}/auth/logout`, 
        { refreshToken }, 
        { withCredentials: true }
      );
      
      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      return true;
    } catch (error) {
      // Even if API call fails, clear local tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // Don't throw error - let the component handle navigation
      return false;
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }



  /**
   * Refresh access token
   */
  async refreshToken(refreshToken) {
    try {
      const response = await api.post('/auth/refresh-token', { refreshToken });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify email (when email verification is enabled)
   */
  async verifyEmail(token) {
    try {
      const response = await api.post('/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify phone OTP and complete registration (creates user only after OTP verification)
   */
  async verifyPhoneOTP(phone, otp, userData) {
    try {
      const response = await api.post('/auth/verify-phone-otp', { phone, otp, userData });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Resend phone OTP for registration
   */
  async resendPhoneOTP(phone, userData) {
    try {
      const response = await api.post('/auth/resend-phone-otp', { phone, userData });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Address Management Methods
   */

  /**
   * Get user addresses
   */
  async getAddresses() {
    try {
      const response = await api.get('/auth/addresses');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add new address
   */
  async addAddress(addressData) {
    try {
      const response = await api.post('/auth/addresses', addressData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update address
   */
  async updateAddress(addressId, addressData) {
    try {
      const response = await api.put(`/auth/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete address
   */
  async deleteAddress(addressId) {
    try {
      const response = await api.delete(`/auth/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set address as default
   */
  async setDefaultAddress(addressId) {
    try {
      const response = await api.put(`/auth/addresses/${addressId}/default`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new AuthService();


